import type { ReactNode } from 'react';
import { Tooltip } from '@vd/ui';
import {
  ArrowDownUp, CircleCheck, CircleX, FileCode2, Globe, Hourglass, Image as ImageIcon, Info, Lightbulb,
  MousePointerClick, Move, Package, Paintbrush, Server, Snail, Timer,
} from 'lucide-react';
import type { NetworkRequest, PerformanceSnapshot } from '@vd/types';
import styles from './PerformanceTab.module.css';

/**
 * The Performance tab (Jason, 23 and 25 Oct): how the page loaded and
 * answered, judged against Google's published thresholds where there are
 * any, with what each reading means and what usually helps, from what the
 * capture already holds. Nothing here asks the extension for more.
 */

/** A time in milliseconds, as seconds once it reaches one. */
export function formatMs(ms?: number): string {
  if (ms === undefined) return '-';
  return ms >= 1000 ? `${(ms / 1000).toFixed(2)}s` : `${Math.round(ms)}ms`;
}

/** A byte count as people read it. */
function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

type Rating = 'good' | 'fair' | 'poor';
const RATING_NAME: Record<Rating, string> = { good: 'Good', fair: 'Needs improvement', poor: 'Poor' };

type Rated = 'ttfb' | 'fcp' | 'lcp' | 'inp' | 'cls';

/**
 * Each rated measure: Google's thresholds (web.dev), what it measures, what a
 * reading short of good means, and what usually helps.
 */
const MEASURES: Record<Rated, {
  name: string;
  what: string;
  good: number;
  poor: number;
  /** What a reading short of good says about the page. */
  short: string;
  helps: string[];
}> = {
  ttfb: {
    name: 'Time to first byte',
    what: 'How long the server took to start sending the page.',
    good: 800, poor: 1800,
    short: 'The server, or the network on the way to it, was slow to start answering, and everything else on the page waited for it.',
    helps: [
      'Cache the page, or the data it is built from, on the server or a CDN.',
      'Cut redirects before the page.',
      'Look for slow database queries or API calls the page waits on before it can answer.',
    ],
  },
  fcp: {
    name: 'First contentful paint',
    what: 'When the first text or image of the page showed.',
    good: 1800, poor: 3000,
    short: 'The person looked at a blank page for longer than they should have.',
    helps: [
      'Answer faster from the server (see TTFB).',
      'Load fewer stylesheets and scripts in the head, or mark scripts defer or async.',
      'Load web fonts with font-display: swap, so text shows before its font arrives.',
    ],
  },
  lcp: {
    name: 'Largest contentful paint',
    what: 'When the largest image or block of text in view showed. Google uses it for how fast a page feels.',
    good: 2500, poor: 4000,
    short: 'The main content, usually the hero image or headline, took too long to show.',
    helps: [
      'Make the largest image smaller (a modern format at the size shown) and give it fetchpriority="high".',
      'Do not lazy-load an image at the top of the page.',
      'Cut stylesheets and scripts that block the page from drawing.',
      'A slow server answer (TTFB) delays everything after it.',
    ],
  },
  inp: {
    name: 'Interaction to next paint',
    what: 'How long the slowest click, tap or key press took to show its result.',
    good: 200, poor: 500,
    short: 'At least one click or key press felt slow: the page took a while to show that anything happened.',
    helps: [
      'Break long JavaScript tasks into smaller ones (see the long tasks below).',
      'In event handlers, show the result first and do the rest of the work after.',
      'Look for heavy re-renders or layout work triggered by the interaction.',
    ],
  },
  cls: {
    name: 'Cumulative layout shift',
    what: 'How much the page moved around unexpectedly while it was used. A score, not a time.',
    good: 0.1, poor: 0.25,
    short: 'Content jumped under the person, which makes them lose their place or click the wrong thing.',
    helps: [
      'Give images, videos, ads and embeds a width and height or an aspect ratio.',
      'Do not insert banners or content above what is already showing.',
      'Use fallback fonts sized like the web font, so text does not reflow when it loads.',
    ],
  },
};

function rate(measure: Rated, value: number | undefined): Rating | undefined {
  if (value === undefined) return undefined;
  const { good, poor } = MEASURES[measure];
  return value <= good ? 'good' : value <= poor ? 'fair' : 'poor';
}

function formatMeasure(measure: Rated, value: number): string {
  return measure === 'cls' ? value.toFixed(3) : formatMs(value);
}

/** A rated measure's explanation: what it is, the thresholds, and when short of good, what that means and what helps. */
function MeasureHint({ measure, value, clue }: { measure: Rated; value: number | undefined; clue?: string }) {
  const m = MEASURES[measure];
  const rating = rate(measure, value);
  return (
    <span className={styles.hint}>
      <strong className={styles.hintTitle}>{m.name} ({measure.toUpperCase()})</strong>
      <span>{m.what}</span>
      <span className={styles.hintScale}>
        Good up to {formatMeasure(measure, m.good)}, poor over {formatMeasure(measure, m.poor)}.
      </span>
      {rating && rating !== 'good' && value !== undefined && (
        <span>
          <strong>{RATING_NAME[rating]}, at {formatMeasure(measure, value)}.</strong> {m.short}
        </span>
      )}
      {clue && <span>{clue}</span>}
      {rating && rating !== 'good' && (
        <>
          <strong className={styles.hintTitle}>What usually helps</strong>
          {/* Plain lines, not a list: the hint is also read inside its tile, which is itself a list item. */}
          <span className={styles.hintList}>
            {m.helps.map((help) => <span key={help}>{help}</span>)}
          </span>
        </>
      )}
    </span>
  );
}

function PlainHint({ title, children }: { title: string; children: ReactNode }) {
  return (
    <span className={styles.hint}>
      <strong className={styles.hintTitle}>{title}</strong>
      <span>{children}</span>
    </span>
  );
}

function PerfTile({ label, icon, value, hint, rating, note }: {
  label: string;
  icon: ReactNode;
  value: string;
  hint: ReactNode;
  rating?: Rating;
  note?: string;
}) {
  return (
    <Tooltip content={hint} multiline>
      {/* Focusable so a keyboard opens the same explanation a pointer does
          (the tooltip opens on focus), and the rating is a word, not only a
          colour (review round 2). */}
      <div className={styles.perfTile} data-rating={rating} role="listitem" tabIndex={0}>
        <div className={styles.perfTop}>
          <span className={styles.perfIcon} aria-hidden>{icon}</span>
          <span className={styles.perfLabel}>{label}</span>
          <Info size={12} className={styles.perfInfo} aria-hidden />
        </div>
        <div className={styles.perfVal}>{value}</div>
        {rating && <span className={styles.perfBadge}>{RATING_NAME[rating]}</span>}
        {note && <div className={styles.perfNote}>{note}</div>}
        {/* The hint for a screen reader, which the tooltip is not tied to. */}
        <span className={styles.srOnly}>{hint}</span>
      </div>
    </Tooltip>
  );
}

/** Worst first, so the summary takes the colour of what needs attention. */
const WORST: Rating[] = ['poor', 'fair', 'good'];

/**
 * LCP, INP and CLS, the three Google calls Core Web Vitals: whether the page
 * passes, and which need attention.
 */
function VitalsSummary({ perf }: { perf: PerformanceSnapshot }) {
  const vitals = (['lcp', 'inp', 'cls'] as const).map((measure) => ({ measure, value: perf[measure], rating: rate(measure, perf[measure]) }));
  const measured = vitals.filter((vital) => vital.rating !== undefined);
  if (measured.length === 0) return null;
  const attention = measured.filter((vital) => vital.rating !== 'good');
  const worst = WORST.find((rating) => measured.some((vital) => vital.rating === rating))!;
  const names = (list: typeof vitals) => list.map((vital) => vital.measure.toUpperCase()).join(', ');
  const missing = vitals.filter((vital) => vital.rating === undefined);
  const headline = attention.length === 0
    ? (missing.length === 0 ? 'Passes Core Web Vitals' : 'Core Web Vitals measured are good')
    : `${names(attention)} ${attention.length === 1 ? 'needs' : 'need'} attention`;
  return (
    <section className={styles.summary} data-rating={worst} aria-label="Core Web Vitals">
      <div className={styles.summaryText}>
        <strong className={styles.summaryHead}>{headline}</strong>
        <span className={styles.summarySub}>
          LCP, INP and CLS are the measures Google uses for how a page feels to the person using it.
          {missing.length > 0 && ` Not measured here: ${names(missing)}${missing.some((vital) => vital.measure === 'inp') ? ' (INP needs a click, tap or key press while the page is watched)' : ''}.`}
        </span>
      </div>
      <div className={styles.summaryChips} role="list">
        {vitals.map((vital) => (
          <span key={vital.measure} className={styles.chip} data-rating={vital.rating} role="listitem">
            <span className={styles.chipName}>{vital.measure.toUpperCase()}</span>
            {vital.value === undefined ? 'Not measured' : `${formatMeasure(vital.measure, vital.value)} · ${RATING_NAME[vital.rating!]}`}
          </span>
        ))}
      </div>
    </section>
  );
}

/** The host a request went to, or null for an address that is not one. */
function hostOf(url: string): string | null {
  try { return new URL(url).hostname.toLowerCase(); } catch { return null; }
}

/**
 * The site part of a host name: its last two labels. Good enough to tell a
 * page's own API (api.shop.test) from someone else's (cdn.example.com), and
 * says so where it is shown; a public suffix list is not worth shipping for it.
 */
function siteOf(host: string): string {
  return host.replace(/^www\./, '').split('.').slice(-2).join('.');
}

type Kind = 'document' | 'script' | 'style' | 'image' | 'font' | 'media' | 'api' | 'other';
const KIND_LABEL: Record<Kind, string> = {
  document: 'Pages', script: 'Scripts', style: 'Stylesheets', image: 'Images', font: 'Fonts', media: 'Video and audio', api: 'API calls', other: 'Other',
};
const KIND_ORDER: Kind[] = ['api', 'script', 'style', 'image', 'font', 'media', 'document', 'other'];

/**
 * A request as this tab reads it. Two shapes are stored: the extension's
 * (method, headers, `responseSize`) and the widget's and recordings', read
 * from the browser's resource timing (`initiatorType`, `transferSize`, no
 * method, no headers, a status only where the browser gives one). Each field
 * here is only what the row says, and missing when it does not (Jason, 25 Oct:
 * a widget ticket's tab failed on the missing method).
 */
interface Req {
  id: string;
  method: string;
  url: string;
  status?: number;
  durationMs: number;
  bytes?: number;
  kind: Kind;
}

/** What it fetched: its content type, else its address, else what the browser says asked for it, else its method. */
function kindOf(row: Partial<NetworkRequest> & { initiatorType?: unknown }, url: string, method: string): Kind {
  const header = Object.entries(row.responseHeaders ?? {}).find(([name]) => name.toLowerCase() === 'content-type')?.[1];
  const type = typeof header === 'string' ? header.toLowerCase() : '';
  if (type.includes('html')) return 'document';
  if (type.includes('javascript') || type.includes('ecmascript')) return 'script';
  if (type.includes('css')) return 'style';
  if (type.startsWith('image/')) return 'image';
  if (type.startsWith('font/') || type.includes('woff')) return 'font';
  if (type.startsWith('video/') || type.startsWith('audio/')) return 'media';
  if (type.includes('json') || type.includes('xml') || type.includes('graphql')) return 'api';
  let path = '';
  try { path = new URL(url).pathname.toLowerCase(); } catch { /* not an address */ }
  if (/\.(m?js)$/.test(path)) return 'script';
  if (/\.css$/.test(path)) return 'style';
  if (/\.(png|jpe?g|gif|webp|avif|svg|ico)$/.test(path)) return 'image';
  if (/\.(woff2?|ttf|otf|eot)$/.test(path)) return 'font';
  if (/\.(mp4|webm|mp3|m4a|ogg|wav)$/.test(path)) return 'media';
  // The browser's own word for what asked for it. "css" and "link" can be
  // anything a stylesheet or a preload fetched, so they say nothing here.
  const initiator = typeof row.initiatorType === 'string' ? row.initiatorType : '';
  if (initiator === 'script') return 'script';
  if (initiator === 'img' || initiator === 'image') return 'image';
  if (initiator === 'fetch' || initiator === 'xmlhttprequest' || initiator === 'beacon') return 'api';
  if (initiator === 'video' || initiator === 'audio') return 'media';
  if (initiator === 'iframe' || initiator === 'navigation') return 'document';
  if ((method && method !== 'GET') || path.includes('/api/')) return 'api';
  return 'other';
}

/** The size the row gives: the answer's size, else the bytes the browser fetched (0 there means cached or not told). */
function sizeOf(row: Partial<NetworkRequest> & { transferSize?: unknown }): number | undefined {
  const size = row.responseSize;
  if (typeof size === 'number' && Number.isFinite(size) && size >= 0) return size;
  const fetched = row.transferSize;
  if (typeof fetched === 'number' && Number.isFinite(fetched) && fetched > 0) return fetched;
  return undefined;
}

/** Each stored row as this tab reads it; a row without an address is left out. */
function readRequests(network: readonly unknown[]): Req[] {
  const requests: Req[] = [];
  network.forEach((value, index) => {
    if (!value || typeof value !== 'object') return;
    const row = value as Partial<NetworkRequest> & { initiatorType?: unknown; transferSize?: unknown };
    if (typeof row.url !== 'string' || row.url === '') return;
    const method = typeof row.method === 'string' ? row.method.toUpperCase() : '';
    const status = typeof row.status === 'number' && Number.isFinite(row.status) ? row.status : undefined;
    const durationMs = typeof row.durationMs === 'number' && Number.isFinite(row.durationMs) && row.durationMs >= 0 ? row.durationMs : 0;
    const bytes = sizeOf(row);
    requests.push({
      id: typeof row.id === 'string' && row.id ? row.id : `row-${index}`,
      method,
      url: row.url,
      ...(status !== undefined ? { status } : {}),
      durationMs,
      ...(bytes !== undefined ? { bytes } : {}),
      kind: kindOf(row, row.url, method),
    });
  });
  return requests;
}

/** Failed: an error status, or none at all where the row's kind records one (0). A row that never said is not failed. */
function failedRequest(request: Req): boolean {
  return request.status !== undefined && (request.status === 0 || request.status >= 400);
}

/** A request's address without its query, for a row; the whole of it is the row's title. */
function shortUrl(url: string): string {
  try {
    const parsed = new URL(url);
    return `${parsed.host}${parsed.pathname}`;
  } catch {
    return url;
  }
}

/** Method and address, the method left out where the row has none. */
function describe(request: Req): string {
  return request.method ? `${request.method} ${request.url}` : request.url;
}

/**
 * Where to start: the few readings that most explain a slow page, said
 * plainly, from what was captured. Each rule names a reading the tab shows.
 */
function insightsFor(perf: PerformanceSnapshot, network: Req[], longest: number): string[] {
  const found: string[] = [];
  const ttfb = rate('ttfb', perf.ttfb);
  if (ttfb && ttfb !== 'good') {
    found.push(`The server took ${formatMs(perf.ttfb)} to start answering, and nothing on the page could load before that. Start with the server.`);
  }
  const lcp = rate('lcp', perf.lcp);
  if (lcp && lcp !== 'good' && ttfb !== 'fair' && ttfb !== 'poor') {
    found.push(`The main content showed at ${formatMs(perf.lcp)}. Look at the largest image or headline in view, and what loads before it.`);
  }
  if (longest > 200) {
    found.push(`The page was busy for ${formatMs(longest)} at a stretch. A click or key press in that time waits for it.`);
  }
  const cls = rate('cls', perf.cls);
  if (cls && cls !== 'good') {
    found.push(`The page moved under the person (layout shift ${perf.cls!.toFixed(3)}). Check images, ads and banners that load without a space kept for them.`);
  }
  const failed = network.filter(failedRequest).length;
  if (failed > 0) {
    found.push(`${failed} ${failed === 1 ? 'request' : 'requests'} failed or had no answer. The Network tab has each one.`);
  }
  const slowest = network.reduce<Req | null>((most, request) => (!most || request.durationMs > most.durationMs ? request : most), null);
  if (slowest && slowest.durationMs >= 1000) {
    found.push(`The slowest request took ${formatMs(slowest.durationMs)}: ${slowest.method ? `${slowest.method} ` : ''}${shortUrl(slowest.url)}.`);
  }
  const bytes = network.reduce((sum, request) => sum + (request.bytes ?? 0), 0);
  if (Number.isFinite(bytes) && bytes > 3 * 1024 * 1024) {
    found.push(`The page downloaded ${formatBytes(bytes)}, which is heavy on a phone connection.`);
  }
  return found.slice(0, 4);
}

/** The page's milestones on one scale, in the order they happened. */
function LoadTimeline({ perf }: { perf: PerformanceSnapshot }) {
  const steps = [
    { label: 'Server answered', short: 'TTFB', value: perf.ttfb, rating: rate('ttfb', perf.ttfb) },
    { label: 'First content', short: 'FCP', value: perf.fcp, rating: rate('fcp', perf.fcp) },
    { label: 'HTML ready', short: 'DOM ready', value: perf.domContentLoaded, rating: undefined },
    { label: 'Main content', short: 'LCP', value: perf.lcp, rating: rate('lcp', perf.lcp) },
    { label: 'Fully loaded', short: 'Load', value: perf.navigation?.loadEvent, rating: undefined },
  ].filter((step): step is typeof step & { value: number } => step.value !== undefined)
    .sort((a, b) => a.value - b.value);
  if (steps.length < 2) return null;
  const end = Math.max(...steps.map((step) => step.value), 1);
  return (
    <section aria-label="Load timeline">
      <h4 className={styles.perfHeading}>Load timeline</h4>
      <div className={styles.bars} role="list">
        {steps.map((step) => (
          <div key={step.short} className={styles.barRow} role="listitem">
            <span className={styles.barLabel}>{step.label} <span className={styles.barShort}>{step.short}</span></span>
            <span className={styles.barTrack}>
              <span className={styles.barFill} data-rating={step.rating} style={{ width: `${Math.max(1.5, (step.value / end) * 100)}%` }} />
            </span>
            <span className={styles.barValue}>{formatMs(step.value)}</span>
          </div>
        ))}
      </div>
    </section>
  );
}

/** How many requests of each kind, and their size where the browser said. */
function RequestKinds({ network }: { network: Req[] }) {
  const byKind = new Map<Kind, { count: number; bytes: number }>();
  for (const request of network) {
    const entry = byKind.get(request.kind) ?? { count: 0, bytes: 0 };
    entry.count += 1;
    entry.bytes += request.bytes ?? 0;
    byKind.set(request.kind, entry);
  }
  const most = Math.max(...[...byKind.values()].map((entry) => entry.count), 1);
  const rows = KIND_ORDER.filter((kind) => byKind.has(kind));
  return (
    <div>
      <h5 className={styles.subHeading}>By type</h5>
      <div className={styles.bars} role="list">
        {rows.map((kind) => {
          const entry = byKind.get(kind)!;
          return (
            <div key={kind} className={styles.barRow} role="listitem">
              <span className={styles.barLabel}>{KIND_LABEL[kind]}</span>
              <span className={styles.barTrack}>
                <span className={styles.barFill} data-kind={kind} style={{ width: `${(entry.count / most) * 100}%` }} />
              </span>
              <span className={styles.barValue}>
                {entry.count}{entry.bytes > 0 && Number.isFinite(entry.bytes) ? ` · ${formatBytes(entry.bytes)}` : ''}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

/** The slowest or largest few requests, each with its address in full on hover. */
function RequestList({ title, requests, figure }: { title: string; requests: Req[]; figure: (request: Req) => string }) {
  if (requests.length === 0) return null;
  return (
    <div>
      <h5 className={styles.subHeading}>{title}</h5>
      <ol className={styles.requestList}>
        {requests.map((request) => (
          <li key={request.id} className={styles.requestRow} title={describe(request)}>
            <span className={styles.method}>{request.method}</span>
            <span className={styles.requestUrl}>{shortUrl(request.url)}</span>
            <span className={styles.requestStatus} data-failed={failedRequest(request) ? '' : undefined}>
              {request.status === 0 ? 'no answer' : request.status}
            </span>
            <span className={styles.requestFigure}>{figure(request)}</span>
          </li>
        ))}
      </ol>
    </div>
  );
}

/** When the main thread was busy, on the span the page was watched, and the longest few. */
function LongTasks({ tasks }: { tasks: PerformanceSnapshot['longTasks'] }) {
  const end = Math.max(...tasks.map((task) => task.startTime + task.durationMs), 1);
  const longestFirst = [...tasks].sort((a, b) => b.durationMs - a.durationMs).slice(0, 5);
  return (
    <section aria-label="Long tasks">
      <h4 className={styles.perfHeading}>Long tasks ({tasks.length})</h4>
      <p className={styles.sectionNote}>
        Each mark is a stretch over 50 ms when the page could not answer a click or key press. Over 200 ms is easy to feel.
      </p>
      <div className={styles.taskStrip} aria-hidden>
        {tasks.map((task, i) => (
          <span
            key={i}
            className={styles.task}
            data-rating={task.durationMs > 200 ? 'poor' : 'fair'}
            style={{ left: `${(task.startTime / end) * 100}%`, width: `max(2px, ${(task.durationMs / end) * 100}%)` }}
          />
        ))}
      </div>
      <div className={styles.taskAxis} aria-hidden>
        <span>0s</span>
        <span>{formatMs(end)}</span>
      </div>
      <ol className={styles.requestList} aria-label="Longest tasks">
        {longestFirst.map((task, i) => (
          <li key={i} className={styles.taskRow}>
            <span className={styles.taskDot} data-rating={task.durationMs > 200 ? 'poor' : 'fair'} aria-hidden />
            <span className={styles.requestFigure}>{formatMs(task.durationMs)}</span>
            <span className={styles.taskAt}>at {formatMs(task.startTime)} after the page started loading</span>
          </li>
        ))}
      </ol>
    </section>
  );
}

/**
 * The page's timings, its responsiveness and its requests. Total blocking
 * time and the longest task come from the long tasks already captured; the
 * request figures from the Network tab's own list, so the two never disagree.
 */
export function PerformanceTab({ perf, hasPerf, network: stored, pageUrl }: {
  perf: PerformanceSnapshot;
  hasPerf: boolean;
  /** The ticket's network rows as stored: either shape (see `Req`), read here and nowhere else. */
  network: readonly unknown[];
  /** The page the capture was of, to tell its own requests from other sites'. */
  pageUrl?: string;
}) {
  const network = readRequests(stored);
  // The part of each long task past 50 ms, which is the time input waited:
  // over every task captured, where Lighthouse's TBT counts only those after
  // first paint, so it is shown and not rated against TBT's thresholds (review).
  const blocking = perf.longTasks.reduce((sum, task) => sum + Math.max(0, task.durationMs - 50), 0);
  const longest = perf.longTasks.reduce((most, task) => Math.max(most, task.durationMs), 0);
  const failed = network.filter(failedRequest).length;
  const sized = network.filter((request) => request.bytes !== undefined);
  const transferred = sized.reduce((sum, request) => sum + (request.bytes ?? 0), 0);
  const slowestFirst = [...network].sort((a, b) => b.durationMs - a.durationMs);
  const slowest = slowestFirst[0] ?? null;
  const pageHost = pageUrl ? hostOf(pageUrl) : null;
  const pageSite = pageHost ? siteOf(pageHost) : null;
  const otherHosts = new Map<string, number>();
  if (pageSite) {
    for (const request of network) {
      const host = hostOf(request.url);
      if (host && siteOf(host) !== pageSite) otherHosts.set(host, (otherHosts.get(host) ?? 0) + 1);
    }
  }
  const otherRequests = [...otherHosts.values()].reduce((sum, count) => sum + count, 0);
  const insights = hasPerf || network.length > 0 ? insightsFor(perf, network, longest) : [];
  if (!hasPerf && network.length === 0) return null;
  return (
    <div className={styles.perfSections}>
      {hasPerf && <VitalsSummary perf={perf} />}
      {insights.length > 0 && (
        <section className={styles.insights} aria-label="Where to start">
          <h4 className={styles.perfHeading}><Lightbulb size={13} aria-hidden /> Where to start</h4>
          <ul className={styles.insightList}>
            {insights.map((insight) => <li key={insight}>{insight}</li>)}
          </ul>
        </section>
      )}
      {hasPerf && (
        <section aria-label="Page load">
          <h4 className={styles.perfHeading}>Page load</h4>
          <div className={styles.perfGrid} role="list">
            <PerfTile label="TTFB" icon={<Server size={14} />} value={formatMs(perf.ttfb)} rating={rate('ttfb', perf.ttfb)} hint={<MeasureHint measure="ttfb" value={perf.ttfb} />} />
            {/* First paint and LCP count from when a prerendered page was shown,
                as Google's web-vitals does: 0 there is the wait the person had.
                TTFB and DOM ready came before anyone was looking, so the
                collectors leave them out there instead. */}
            <PerfTile label="FCP" icon={<Paintbrush size={14} />} value={formatMs(perf.fcp)} rating={rate('fcp', perf.fcp)} hint={<MeasureHint measure="fcp" value={perf.fcp} />} />
            <PerfTile
              label="DOM ready"
              icon={<FileCode2 size={14} />}
              value={formatMs(perf.domContentLoaded)}
              hint={<PlainHint title="DOM ready">When the page's HTML had been read and its DOMContentLoaded handlers had run. Not rated: there is no published threshold. Much later than first paint usually means heavy scripts run as the page starts.</PlainHint>}
            />
            <PerfTile
              label="LCP"
              icon={<ImageIcon size={14} />}
              value={formatMs(perf.lcp)}
              rating={rate('lcp', perf.lcp)}
              hint={<MeasureHint
                measure="lcp"
                value={perf.lcp}
                clue={perf.ttfb !== undefined && perf.lcp !== undefined && rate('lcp', perf.lcp) !== 'good'
                  ? `The server's first byte came at ${formatMs(perf.ttfb)} of that.`
                  : undefined}
              />}
            />
            <PerfTile
              label="Load"
              icon={<CircleCheck size={14} />}
              value={formatMs(perf.navigation?.loadEvent)}
              hint={<PlainHint title="Load">When the page and everything it asked for at first (images, scripts, styles) had finished. Not rated: a page can feel ready well before this.</PlainHint>}
            />
          </div>
        </section>
      )}
      {hasPerf && <LoadTimeline perf={perf} />}
      {hasPerf && (
        <section aria-label="Responsiveness">
          <h4 className={styles.perfHeading}>Responsiveness</h4>
          <div className={styles.perfGrid} role="list">
            <PerfTile
              label="INP"
              icon={<MousePointerClick size={14} />}
              value={formatMs(perf.inp)}
              rating={rate('inp', perf.inp)}
              hint={<MeasureHint
                measure="inp"
                value={perf.inp}
                clue={perf.inp === undefined ? 'Not measured: it needs a click, tap or key press while the page is watched.' : undefined}
              />}
            />
            <PerfTile label="CLS" icon={<Move size={14} />} value={perf.cls === undefined ? '-' : perf.cls.toFixed(3)} rating={rate('cls', perf.cls)} hint={<MeasureHint measure="cls" value={perf.cls} />} />
            <PerfTile
              label="Blocking"
              icon={<Hourglass size={14} />}
              value={perf.longTasks.length > 0 ? formatMs(blocking) : '-'}
              hint={<PlainHint title="Blocking time">How long the captured long tasks kept the page from answering input: each one's time past 50 ms, added up over the whole time the page was watched. Lighthouse's total blocking time counts only from first paint until the page is interactive, so the two can differ a lot on a page used for a while.</PlainHint>}
            />
            <PerfTile
              label="Longest task"
              icon={<Timer size={14} />}
              value={perf.longTasks.length > 0 ? formatMs(longest) : '-'}
              rating={perf.longTasks.length > 0 ? (longest > 200 ? 'poor' : longest > 100 ? 'fair' : 'good') : undefined}
              hint={<PlainHint title="Longest task">The longest stretch the page's main thread was busy and could not answer a click or key press. Under 100 ms goes unnoticed; over 200 ms is easy to feel. The long tasks below say when.</PlainHint>}
            />
          </div>
        </section>
      )}
      {network.length > 0 && (
        <section aria-label="Requests">
          <h4 className={styles.perfHeading}>Requests</h4>
          <div className={styles.perfGrid} role="list">
            <PerfTile
              label="Requests"
              icon={<ArrowDownUp size={14} />}
              value={String(network.length)}
              hint={<PlainHint title="Requests">Requests the page made while it was being captured. The Network tab lists each one.</PlainHint>}
            />
            <PerfTile
              label="Failed or no answer"
              icon={<CircleX size={14} />}
              value={String(failed)}
              rating={failed > 0 ? 'poor' : 'good'}
              hint={<PlainHint title="Failed or no answer">Requests that answered with an error (400 and up), or had no answer: failed, blocked, or still waiting when captured. Any failure here is worth a look in the Network tab.</PlainHint>}
            />
            <PerfTile
              label="Response size"
              icon={<Package size={14} />}
              value={sized.length > 0 && Number.isFinite(transferred) ? formatBytes(transferred) : '-'}
              note={sized.length > 0 && Number.isFinite(transferred) && sized.length < network.length ? `${sized.length} of ${network.length} requests` : undefined}
              hint={<PlainHint title="Response size">The size of the answers, added up over the requests the browser reported a size for. A file served from the browser's cache, or from another site that does not share its timing, reports none. A few megabytes or more is slow to load on a phone connection.</PlainHint>}
            />
            <PerfTile
              label="Slowest"
              icon={<Snail size={14} />}
              value={slowest ? formatMs(slowest.durationMs) : '-'}
              hint={<PlainHint title="Slowest request">{slowest ? describe(slowest) : 'The slowest request.'}</PlainHint>}
            />
            {pageSite && (
              <PerfTile
                label="Other sites"
                icon={<Globe size={14} />}
                value={String(otherHosts.size)}
                note={otherHosts.size > 0 ? `${otherRequests} ${otherRequests === 1 ? 'request' : 'requests'}` : undefined}
                hint={<PlainHint title="Other sites">
                  {otherHosts.size === 0
                    ? `Every request went to ${pageSite}, the page's own site.`
                    : `Hosts other than ${pageSite} the page called: ${[...otherHosts.keys()].slice(0, 6).join(', ')}${otherHosts.size > 6 ? ', and more' : ''}. Each one is another connection to set up, and a script from one can slow the page.`}
                </PlainHint>}
              />
            )}
          </div>
          <div className={styles.requestPanels}>
            <RequestKinds network={network} />
            <RequestList title="Slowest" requests={slowestFirst.slice(0, 5)} figure={(request) => formatMs(request.durationMs)} />
            <RequestList
              title="Largest"
              requests={[...sized].sort((a, b) => (b.bytes ?? 0) - (a.bytes ?? 0)).slice(0, 5)}
              figure={(request) => formatBytes(request.bytes ?? 0)}
            />
          </div>
        </section>
      )}
      {perf.longTasks.length > 0 && <LongTasks tasks={perf.longTasks} />}
    </div>
  );
}
